//
import type { AxiosCollection } from '@/types/Components/Response'
import type { ProxyLogItem } from '@/types/Models/ProxyLog'
import type { OptimisationPath, Shipment } from '@/types/Models/Shipment'
import type { ShipmentEvent } from '@/types/Models/ShipmentEvent'
import type { TrackingEvent } from '@/types/Models/Tracking'
import type { ShipmentStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { QueryParams as CarrierQueryParams } from '@/const/CarrierMessagesListQueryFilters'
import { QueryParams as MessagesQueryParams } from '@/const/MessagesListQueryFilters'
import { QueryParams as ShipmentsQueryParams } from '@/const/ShipmentsListQueryFilters'
import { catchErrors } from '@/services/Helpers'
import ItemsProxy from '@/proxies/ItemsProxy'
import ProxyLogProxy from '@/proxies/ProxyLogProxy'
import ShipmentProxy from '@/proxies/ShipmentProxy'
import TrackingProxy from '@/proxies/TrackingProxy'
import ShipmentEventProxy from '@/proxies/ShipmentEventProxy'
import ShipmentTransformer from '@/transformers/ShipmentTransformer'

const createDefaultState = (): ShipmentStoreState => ({
  shipments: {},
  shipmentsCount: {},
  shipmentsLoading: false,
  proxyLogs: {},
  proxyLogsCount: {},
  proxyLogsLoading: false,
  trackingEvents: {},
  trackingEventsCount: {},
  trackingEventsLoading: false,
  shipmentEvents: {},
  shipmentEventsCount: {},
  shipmentEventsLoading: false,
  optimisationPaths: {},
  optimisationPathsCount: {},
  optimisationPathsLoading: false,
})

export const useShipmentStore = defineStore('shipment', {
  state: (): ShipmentStoreState => createDefaultState(),
  getters: {
    shipmentsList: (state: ShipmentStoreState) => (id?: string | null) => state.shipments[id || 'root'] || [],
    shipmentsTotal: (state: ShipmentStoreState) => (id?: string | null) => state.shipmentsCount[id || 'root'] || 0,
    proxyLogsList: (state: ShipmentStoreState) => (id?: string | null) => state.proxyLogs[id || 'root'] || [],
    proxyLogsTotal: (state: ShipmentStoreState) => (id?: string | null) => state.proxyLogsCount[id || 'root'] || 0,
    trackingEventsList: (state: ShipmentStoreState) => (id?: string | null) => state.trackingEvents[id || 'root'] || [],
    trackingEventsTotal: (state: ShipmentStoreState) => (id?: string | null) =>
      state.trackingEventsCount[id || 'root'] || 0,
    shipmentEventsList: (state: ShipmentStoreState) => (id?: string | null) => state.shipmentEvents[id || 'root'] || [],
    shipmentEventsTotal: (state: ShipmentStoreState) => (id?: string | null) =>
      state.shipmentEventsCount[id || 'root'] || 0,
    optimisationPathsList: (state: ShipmentStoreState) => (id?: string | null) =>
      state.optimisationPaths[id || 'root'] || [],
    optimisationPathsTotal: (state: ShipmentStoreState) => (id?: string | null) =>
      state.optimisationPathsCount[id || 'root'] || 0,
  },
  actions: {
    fetchShipments(params: URLSearchParams) {
      this.shipmentsLoading = true
      const id = params.get(ShipmentsQueryParams.SHIPMENT_NUMBER) ?? params.get('item[]')
      return new ShipmentProxy()
        .getList(params)
        .then((response: AxiosCollection<Shipment>) => {
          const key = id || 'root'
          this.shipments[key] = response.data
          this.shipmentsCount[key] = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.shipmentsLoading = false
        })
    },
    //used on the proxy log tab and shipment details outgoing messages tab
    fetchProxyLogs(params: URLSearchParams) {
      this.proxyLogsLoading = true
      const id = params.get(MessagesQueryParams.SHIPMENT_NUMBER) ?? params.get('item[]')
      return new ProxyLogProxy()
        .getList(params)
        .then((response: AxiosCollection<ProxyLogItem>) => {
          const key = id || 'root'
          this.proxyLogs[key] = response.data
          this.proxyLogsCount[key] = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.proxyLogsLoading = false
        })
    },
    fetchShipmentEvents(params: URLSearchParams) {
      this.shipmentEventsLoading = true
      const id = params.get(CarrierQueryParams.ITEM) ?? params.get('item[]')
      return new ShipmentEventProxy()
        .getList(params)
        .then((response: AxiosCollection<ShipmentEvent>) => {
          const key = id || 'root'
          this.shipmentEvents[key] = response.data
          this.shipmentEventsCount[key] = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.shipmentEventsLoading = false
        })
    },
    fetchTrackingEvents(shipmentNumber: string) {
      this.trackingEventsLoading = true
      return new TrackingProxy()
        .find(shipmentNumber)
        .then((response: AxiosCollection<TrackingEvent>) => {
          const key = shipmentNumber || 'root'
          this.trackingEvents[key] = response.data
          this.trackingEventsCount[key] = response.count
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.trackingEventsLoading = false
        })
    },
    fetchOptimisationSummary(id: string, summary: any[]) {
      this.optimisationPathsLoading = true
      return new ItemsProxy()
        .optimizationPath(id)
        .then((response: AxiosCollection<OptimisationPath>) => {
          const key = id || 'root'
          const summaryPaths = ShipmentTransformer.normalizeOptimisationSummary(summary)
          this.optimisationPaths[key] = [...response.data, ...summaryPaths].sort((a, b) =>
            (a.price ?? 0) > (b.price ?? 0) ? 1 : -1,
          )
          this.optimisationPathsCount[key] = this.optimisationPaths[key]?.length || 0
          return response
        })
        .catch(catchErrors)
        .finally(() => {
          this.optimisationPathsLoading = false
        })
    },
  },
})

export type ShipmentStore = ReturnType<typeof useShipmentStore>
